import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence, type TargetAndTransition } from "motion/react";
import { X } from "lucide-react";
import { cn } from "@/marketing/lib/utils";

/* ------------------------------------------------------------------ */
/*  Context – pass `open` from Sheet root to SheetContent              */
/* ------------------------------------------------------------------ */
const SheetContext = React.createContext(false);

function Sheet({
  children,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return (
    <SheetContext.Provider value={!!props.open}>
      <SheetPrimitive.Root {...props}>{children}</SheetPrimitive.Root>
    </SheetContext.Provider>
  );
}

const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;
const SheetPortal = SheetPrimitive.Portal;

/* ------------------------------------------------------------------ */
/*  Slide direction helpers                                            */
/* ------------------------------------------------------------------ */
type Side = "top" | "bottom" | "left" | "right";

const slideConfig: Record<Side, { initial: TargetAndTransition; animate: TargetAndTransition; exit: TargetAndTransition }> = {
  right: {
    initial: { x: "100%" },
    animate: { x: 0 },
    exit: { x: "100%" },
  },
  left: {
    initial: { x: "-100%" },
    animate: { x: 0 },
    exit: { x: "-100%" },
  },
  top: {
    initial: { y: "-100%" },
    animate: { y: 0 },
    exit: { y: "-100%" },
  },
  bottom: {
    initial: { y: "100%" },
    animate: { y: 0 },
    exit: { y: "100%" },
  },
};

const sideClasses: Record<Side, string> = {
  top: "tw-inset-x-0 tw-top-0 tw-border-b",
  bottom: "tw-inset-x-0 tw-bottom-0 tw-border-t",
  left: "tw-inset-y-0 tw-left-0 tw-h-full tw-w-3/4 tw-border-r sm:tw-max-w-sm",
  right: "tw-inset-y-0 tw-right-0 tw-h-full tw-w-3/4 tw-border-l sm:tw-max-w-lg",
};

/* ------------------------------------------------------------------ */
/*  SheetContent                                                       */
/* ------------------------------------------------------------------ */
interface SheetContentProps
  extends Omit<React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>, "ref"> {
  side?: Side;
}

const SheetContent = React.forwardRef<
  HTMLDivElement,
  SheetContentProps
>(({ side = "right", className, children, ...props }, ref) => {
  const open = React.useContext(SheetContext);
  const slide = slideConfig[side];

  return (
    <AnimatePresence>
      {open && (
        <SheetPortal forceMount>
          <SheetPrimitive.Overlay forceMount asChild>
            <motion.div
              className="tw-fixed tw-inset-0 tw-z-50 tw-bg-black/80"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
          </SheetPrimitive.Overlay>
          <SheetPrimitive.Content forceMount asChild {...props}>
            <motion.div
              ref={ref}
              className={cn(
                "tw-fixed tw-z-50 tw-gap-4 tw-bg-background tw-shadow-lg",
                sideClasses[side],
                className,
              )}
              initial={slide.initial}
              animate={slide.animate}
              exit={slide.exit}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
            >
              <SheetPrimitive.Close className="tw-absolute tw-right-4 tw-top-4 tw-rounded-sm tw-opacity-70 tw-ring-offset-background tw-transition-opacity hover:tw-opacity-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2 disabled:tw-pointer-events-none">
                <X className="tw-h-4 tw-w-4" />
                <span className="tw-sr-only">Close</span>
              </SheetPrimitive.Close>
              {children}
            </motion.div>
          </SheetPrimitive.Content>
        </SheetPortal>
      )}
    </AnimatePresence>
  );
});
SheetContent.displayName = "SheetContent";

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */
function SheetHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "tw-flex tw-flex-col tw-space-y-2 tw-text-center sm:tw-text-left",
        className,
      )}
      {...props}
    />
  );
}

function SheetFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "tw-flex tw-flex-col-reverse sm:tw-flex-row sm:tw-justify-end sm:tw-space-x-2",
        className,
      )}
      {...props}
    />
  );
}

const SheetTitle = React.forwardRef<
  React.ComponentRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("tw-text-lg tw-font-semibold tw-text-foreground", className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ComponentRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("tw-text-sm tw-text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
